/**
 * Main Application Component
 * Configures routing and micro-frontend orchestration
 */
import React, { Suspense, lazy, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { ThemeProvider } from './hooks/useTheme';
import { ThemeColorProvider } from './hooks/useThemeColor';
import AccessGate from './components/auth/AccessGate';
import ErrorBoundary from './components/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SmartAI from './components/smartai/SmartAI';
import { store } from './state/store';
import { Provider } from 'react-redux';
import OnboardingLayout from './components/smartai/ComponentOnboarding/OnboardingLayout';
import ComponentOnboardingView from './components/smartai/ComponentOnboarding/ComponentOnboardingView';
import ProcessFilesView from './components/smartai/ProcessFiles/ProcessFilesVIew';
import OnboardInventoryView from './components/smartai/OnboardInventory/OnboardInventoryView';
import { shouldExposePlaywrightHooks } from '@/utils/playwrightHooks';

/**
 * Lazy-loaded components for code splitting
 */
const MainLayout = lazy(() => import('./components/layout/MainLayout'));
const Profile = lazy(() => import('./components/profile/Profile'));
const People = lazy(() => import('./components/people/People'));
const PeopleDetails = lazy(() => import('./components/people/PeopleDetails'));
const DetectRepoView = lazy(
  () => import('./components/smartai/DetectRepo/DetectRepoView'),
);

const queryClient = new QueryClient();

/** Playwright / DEV helper: client-side navigate without wiping Redux. */
function E2eNavigateBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!shouldExposePlaywrightHooks()) return;
    (
      window as unknown as {
        __QTRACE_NAVIGATE__?: (path: string) => void;
      }
    ).__QTRACE_NAVIGATE__ = (path: string) => {
      navigate(path);
    };
    return () => {
      delete (
        window as unknown as { __QTRACE_NAVIGATE__?: (path: string) => void }
      ).__QTRACE_NAVIGATE__;
    };
  }, [navigate]);
  return null;
}
/**
 * Main App Component
 * Provides routing configuration with MFE support
 * @returns {React.FC} Application component
 */
const App: React.FC = () => {
  /**
   * Base path for routing
   * Development: root path for local dev server
   * Production: app-specific path matching Vite config
   */

  const basename = import.meta.env.MODE === 'production' ? '' : '';

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AccessGate>
          <ThemeColorProvider>
            <Provider store={store}>
              <QueryClientProvider client={queryClient}>
                <Router basename={basename}>
                  <E2eNavigateBridge />
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <Suspense fallback={null}>
                          <MainLayout />
                        </Suspense>
                      }>
                      <Route
                        index
                        element={<Navigate to="/sia/components" replace />}
                      />
                      <Route
                        path="/sia/*"
                        element={
                          <Suspense fallback={null}>
                            <SmartAI />
                          </Suspense>
                        }
                      />
                      <Route
                        element={
                          <Suspense fallback={null}>
                            <OnboardingLayout />
                          </Suspense>
                        }>
                        <Route
                          path="/sia/onboarding"
                          element={
                            <Suspense fallback={null}>
                              <ComponentOnboardingView />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/sia/components/process-files"
                          element={
                            <Suspense fallback={null}>
                              <ProcessFilesView />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/sia/components/onboard-inventory"
                          element={
                            <Suspense fallback={null}>
                              <OnboardInventoryView />
                            </Suspense>
                          }
                        />
                      </Route>

                      <Route
                        path="people"
                        element={
                          <Suspense fallback={null}>
                            <People />
                          </Suspense>
                        }
                      />
                      <Route
                        path="people/:id"
                        element={
                          <Suspense fallback={null}>
                            <PeopleDetails />
                          </Suspense>
                        }
                      />
                      <Route
                        path="profile"
                        element={
                          <Suspense fallback={null}>
                            <Profile />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/detect"
                        element={
                          <Suspense fallback={null}>
                            <DetectRepoView />
                          </Suspense>
                        }
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/sia/components" replace />}
                      />
                    </Route>
                  </Routes>
                </Router>
              </QueryClientProvider>
            </Provider>
          </ThemeColorProvider>
        </AccessGate>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
